import { useCallback, useEffect, useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { SECURITY_NAME_ALIAS, SECURITY_NAME_ROW_NUMBER } from 'common/constants/cap-table/capTable';
import { getCellRef } from 'components/FeaturedSpreadsheet/utilities/utilities';
import { AISuggestionsColumnBannerProps } from './AISuggestionsColumnBanner.props';
import {
  PILL_GAP_ABOVE_CELL,
  PILL_HEIGHT,
  PILL_WIDTH,
  pillDividerSx,
  pillHalfSx,
  pillRootSx,
} from './AISuggestionsColumnBanner.styles';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';

interface PendingNameColumn {
  columnLegend: string;
  columnId: number;
  cellKey: string;
}

interface PillPosition {
  top: number;
  left: number;
  width: number;
}

/**
 * Finds columns where the security-name cell has an unaccepted AI suggestion.
 */
const derivePendingNameColumns = (
  cells: Record<string, CapTableCellWithReferences> | undefined
): PendingNameColumn[] => {
  if (!cells) {
    return [];
  }
  const result: PendingNameColumn[] = [];
  Object.entries(cells).forEach(([cellKey, cell]) => {
    if (cell?.alias !== SECURITY_NAME_ALIAS) {
      return;
    }
    const meta = cell.aiFieldMetadata;
    if (!meta || meta.is_accepted) {
      return;
    }
    if (cell.columnId == null || cell.columnLegend == null) {
      return;
    }
    result.push({
      columnLegend: cell.columnLegend as string,
      columnId: cell.columnId as number,
      cellKey,
    });
  });
  return result.sort((a, b) => a.columnLegend.localeCompare(b.columnLegend));
};

const AISuggestionsColumnBanner = ({ cells, containerRef }: AISuggestionsColumnBannerProps) => {
  const { acceptField, dismissField } = useAIFieldAcceptContext();
  const [busyColumnId, setBusyColumnId] = useState<number | null>(null);
  const [positions, setPositions] = useState<Record<string, PillPosition>>({});

  const pendingColumns = useMemo(() => derivePendingNameColumns(cells), [cells]);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    if (!container || pendingColumns.length === 0) {
      setPositions(prev => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const next: Record<string, PillPosition> = {};
    pendingColumns.forEach(({ columnLegend }) => {
      const cellEl = getCellRef(
        containerRef as React.RefObject<HTMLElement>,
        `${columnLegend}${SECURITY_NAME_ROW_NUMBER}`
      );
      if (!cellEl) {
        return;
      }
      const cellWidth = cellEl.offsetWidth;
      if (cellWidth === 0) {
        return;
      }

      let left = 0;
      let top = 0;
      let el: HTMLElement | null = cellEl;
      while (el && el !== container) {
        left += el.offsetLeft - el.scrollLeft;
        top += el.offsetTop - el.scrollTop;
        el = el.offsetParent as HTMLElement | null;
      }

      next[columnLegend] = {
        left: left + cellWidth / 2 - PILL_WIDTH / 2,
        top: top - PILL_HEIGHT - PILL_GAP_ABOVE_CELL,
        width: cellWidth,
      };
    });
    setPositions(next);
  }, [containerRef, pendingColumns]);

  useEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    if (pendingColumns.length === 0) {
      return undefined;
    }
    let frame = 0;
    const schedule = () => {
      if (frame) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = 0;
        recompute();
      });
    };
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const container = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    if (container) {
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(container);
        const tableEl = container.querySelector('[id^="spreadsheet-table__"]');
        if (tableEl) {
          resizeObserver.observe(tableEl);
        }
      }
      mutationObserver = new MutationObserver(schedule);
      mutationObserver.observe(container, { childList: true, subtree: true });
    }
    return () => {
      window.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schedule);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [containerRef, pendingColumns, recompute]);

  const runAction = useCallback(
    async (columnId: number, action: () => Promise<void>) => {
      setBusyColumnId(columnId);
      try {
        await action();
      } finally {
        setBusyColumnId(null);
      }
    },
    []
  );

  if (pendingColumns.length === 0) {
    return null;
  }

  return (
    <>
      {pendingColumns.map(({ columnLegend, columnId }) => {
        const pos = positions[columnLegend];
        if (!pos) {
          return null;
        }
        const isBusy = busyColumnId === columnId;
        return (
          <Box
            key={`ai-name-pill-${columnLegend}`}
            data-testid={`ai-name-pill-${columnLegend}`}
            sx={pillRootSx}
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}>
            <Box
              component="button"
              type="button"
              disabled={isBusy}
              onClick={() => runAction(columnId, () => acceptField(columnId, SECURITY_NAME_ALIAS))}
              data-testid={`ai-name-accept-${columnLegend}`}
              aria-label="Accept AI suggestion"
              title="Accept AI suggestion"
              sx={pillHalfSx}>
              <CheckIcon />
            </Box>
            <Box sx={pillDividerSx} />
            <Box
              component="button"
              type="button"
              disabled={isBusy}
              onClick={() => runAction(columnId, () => dismissField(columnId, SECURITY_NAME_ALIAS))}
              data-testid={`ai-name-dismiss-${columnLegend}`}
              aria-label="Dismiss AI suggestion"
              title="Dismiss AI suggestion"
              sx={pillHalfSx}>
              <CloseIcon />
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default AISuggestionsColumnBanner;
